import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import apiClient from '@/lib/api-client';
import { useI18n } from '@/i18n';
import { useTenant } from '@/contexts/TenantContext';
import { Icon } from '@/components/ui';

interface DashboardSummary {
  dashboard_id: string;
  title: string;
  status: 'draft' | 'active' | 'archived';
  visibility: 'private' | 'tenant';
  classification: 'public' | 'internal' | 'confidential' | 'restricted';
  current_version: number;
  updated_at: string | null;
}

interface DashboardListResponse {
  data: DashboardSummary[];
  total: number;
}

function formatDate(value: string | null, locale: string): string {
  if (!value) return '—';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  return new Intl.DateTimeFormat(locale, { dateStyle: 'medium', timeStyle: 'short' }).format(parsed);
}

export function DashboardsGovernancePage() {
  const { currentTenant } = useTenant();
  const tenantId = currentTenant?.id;
  const { t, locale } = useI18n();
  const [query, setQuery] = useState('');
  const [status, setStatus] = useState('');

  const dashboards = useQuery({
    queryKey: ['dashboards-governance', tenantId, status],
    enabled: Boolean(tenantId),
    queryFn: async () => {
      const { data } = await apiClient.get<DashboardListResponse>(
        `/api/v1/tenants/${tenantId}/dashboards`,
        { params: { limit: 100, ...(status ? { status } : {}) } },
      );
      return data;
    },
  });

  const rows = useMemo(() => {
    const term = query.trim().toLowerCase();
    return (dashboards.data?.data ?? []).filter(
      (row) => !term || row.title.toLowerCase().includes(term),
    );
  }, [dashboards.data?.data, query]);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold text-gray-900 dark:text-gray-100">
          {t('admin.dashboardsGovernance.title')}
        </h1>
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
          {t('admin.dashboardsGovernance.subtitle')}
        </p>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <label className="relative flex-1 min-w-[220px]">
          <span className="sr-only">{t('admin.dashboardsGovernance.search')}</span>
          <input
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder={t('admin.dashboardsGovernance.search')}
            className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-900 outline-none focus:border-blue-500 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-100"
          />
        </label>
        <select
          value={status}
          onChange={(event) => setStatus(event.target.value)}
          aria-label={t('admin.dashboardsGovernance.status')}
          className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-700 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-200"
        >
          <option value="">{t('admin.dashboardsGovernance.allStatuses')}</option>
          <option value="draft">{t('admin.dashboardsGovernance.draft')}</option>
          <option value="active">{t('admin.dashboardsGovernance.active')}</option>
          <option value="archived">{t('admin.dashboardsGovernance.archived')}</option>
        </select>
      </div>

      <div className="overflow-hidden rounded-xl border border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-900">
        {dashboards.isLoading && (
          <p className="p-6 text-sm text-gray-500">{t('admin.dashboardsGovernance.loading')}</p>
        )}
        {dashboards.isError && (
          <p className="p-6 text-sm text-gray-500">{t('admin.dashboardsGovernance.error')}</p>
        )}
        {!dashboards.isLoading && !dashboards.isError && rows.length === 0 && (
          <p className="p-6 text-sm text-gray-500">{t('admin.dashboardsGovernance.empty')}</p>
        )}
        {rows.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead className="bg-gray-50 text-xs uppercase tracking-wide text-gray-500 dark:bg-gray-800 dark:text-gray-400">
                <tr>
                  <th className="px-4 py-3">{t('admin.dashboardsGovernance.name')}</th>
                  <th className="px-4 py-3">{t('admin.dashboardsGovernance.status')}</th>
                  <th className="px-4 py-3">{t('admin.dashboardsGovernance.visibility')}</th>
                  <th className="px-4 py-3">{t('admin.dashboardsGovernance.classification')}</th>
                  <th className="px-4 py-3">{t('admin.dashboardsGovernance.version')}</th>
                  <th className="px-4 py-3">{t('admin.dashboardsGovernance.updated')}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                {rows.map((row) => (
                  <tr key={row.dashboard_id}>
                    <td className="px-4 py-3 font-medium text-gray-900 dark:text-gray-100">
                      {row.title}
                    </td>
                    <td className="px-4 py-3 text-gray-600 dark:text-gray-300">
                      {t(`admin.dashboardsGovernance.${row.status}`)}
                    </td>
                    <td className="px-4 py-3 text-gray-600 dark:text-gray-300">
                      {t(`admin.dashboardsGovernance.${row.visibility}`)}
                    </td>
                    <td className="px-4 py-3 text-gray-600 dark:text-gray-300">
                      {t(`admin.dashboardsGovernance.${row.classification}`)}
                    </td>
                    <td className="px-4 py-3 text-gray-600 dark:text-gray-300">
                      {row.current_version}
                    </td>
                    <td className="px-4 py-3 text-gray-500 dark:text-gray-400">
                      {formatDate(row.updated_at, locale)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <p className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
        <Icon name="info" size={14} />
        {t('admin.dashboardsGovernance.note')}
      </p>
    </div>
  );
}
